import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, ShoppingBag,
  Users, Building2, Calendar, RefreshCw, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

function MetricCard({ title, value, change, changeType, icon: Icon, color }) {
  const isPositive = changeType === 'positive';
  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {change && (
              <div className={`flex items-center gap-1 mt-1 text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                <span>{change}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SimpleBarChart({ data, maxValue }) {
  return (
    <div className="flex items-end gap-2 h-40">
      {data.map((item, index) => (
        <div key={index} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-gradient-to-t from-yellow-500 to-orange-500 rounded-t-sm transition-all"
            style={{ height: `${(item.value / maxValue) * 100}%`, minHeight: '4px' }}
          />
          <span className="text-xs text-slate-500">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const { token } = useOutletContext();
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const fetchAnalytics = useLatestCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/company-admin/analytics?period=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Analytics fetch error:', error);
      toast.error('Could not load analytics');
      setAnalytics(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchAnalytics();
  }, [token, dateRange, fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  const maxRevenue = Math.max(...(analytics?.dailyRevenue || []).map(d => d.value), 1);

  return (
    <div className="space-y-6" data-testid="company-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-slate-400">Platform-wide performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loadError && (
        <Card className="bg-slate-800 border-slate-700" data-testid="company-analytics-error">
          <CardContent className="p-6 text-center">
            <p className="text-slate-300">Could not load analytics.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={fetchAnalytics}>Try again</Button>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Revenue"
          value={`$${analytics?.totalRevenue?.toLocaleString()}`}
          icon={DollarSign}
          color="bg-gradient-to-r from-green-500 to-emerald-500"
        />
        <MetricCard
          title="Total Orders"
          value={analytics?.totalOrders?.toLocaleString()}
          icon={ShoppingBag}
          color="bg-gradient-to-r from-blue-500 to-cyan-500"
        />
        <MetricCard
          title="Active Restaurants"
          value={analytics?.totalRestaurants}
          icon={Building2}
          color="bg-gradient-to-r from-purple-500 to-pink-500"
        />
        <MetricCard
          title="Total Users"
          value={analytics?.totalUsers}
          icon={Users}
          color="bg-gradient-to-r from-orange-500 to-red-500"
        />
      </div>

      {/* Charts Row - drawn only from series the API returns */}
      {(analytics?.dailyRevenue?.length > 0 || analytics?.topRestaurants?.length > 0) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        {analytics?.dailyRevenue?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Revenue Trend</CardTitle>
            <CardDescription className="text-slate-400">Daily revenue for the period</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart data={analytics?.dailyRevenue || []} maxValue={maxRevenue} />
          </CardContent>
        </Card>
        )}

        {/* Top Restaurants */}
        {analytics?.topRestaurants?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Top Restaurants</CardTitle>
            <CardDescription className="text-slate-400">By revenue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics?.topRestaurants?.map((r, index) => (
                <div key={r.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-slate-500">{index + 1}</span>
                      <span className="text-white">{r.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold">${r.revenue?.toLocaleString()}</span>
                  </div>
                  <Progress 
                    value={(r.revenue / (analytics?.topRestaurants?.[0]?.revenue || 1)) * 100} 
                    className="h-2 bg-slate-700 [&>div]:bg-gradient-to-r [&>div]:from-yellow-500 [&>div]:to-orange-500"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}
      </div>
      )}

      {/* Payment Methods */}
      {analytics?.paymentMethods && (
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Payment Methods</CardTitle>
          <CardDescription className="text-slate-400">Distribution across all restaurants</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300">Credit Card</span>
                <span className="text-white font-semibold">{analytics?.paymentMethods?.credit_card}%</span>
              </div>
              <Progress value={analytics?.paymentMethods?.credit_card || 0} className="h-3 bg-slate-700 [&>div]:bg-blue-500" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300">Cash</span>
                <span className="text-white font-semibold">{analytics?.paymentMethods?.cash}%</span>
              </div>
              <Progress value={analytics?.paymentMethods?.cash || 0} className="h-3 bg-slate-700 [&>div]:bg-green-500" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300">Debit Card</span>
                <span className="text-white font-semibold">{analytics?.paymentMethods?.debit_card}%</span>
              </div>
              <Progress value={analytics?.paymentMethods?.debit_card || 0} className="h-3 bg-slate-700 [&>div]:bg-purple-500" />
            </div>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
